import { FormEvent, useState } from "react"
import styled from "styled-components"

import { useLazyGetContractorsQuery } from "store/api/contractorsApi"
import { ApiTypes, UITypes } from "types"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as TopArrow } from "assets/svg/contractor-top-arrow.svg"

import { convertContractorsToTableRows } from "utils/convertData"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AddContractorModal } from "modals/AddContractorModal"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"

const ReverseArrow = styled(TopArrow)`
  transform: rotate(180deg);
`

const Bottom16Button = styled(ButtonNew)`
  margin-right: 15px;
`

interface HeaderProps {
  tableHeaderCells: UITypes.TableHeaderCell[]
  meta?: ApiTypes.Model.Pagination
  onAddContractorSuccess: () => void
}

const Header = ({ tableHeaderCells, meta, onAddContractorSuccess }: HeaderProps) => {
  const [showAddContractorModal, setShowAddContractorModal] = useState(false)
  const { setUrlQueryParams } = useUrlQueryParams()
  const { urlQueryParams } = useUrlQueryParams()

  const getAdditionalParams = (): Record<string, string> => {
    if (urlQueryParams.top15 || urlQueryParams.bottom15) {
      return {
        order_column: "installed",
        order_type: urlQueryParams.top15 ? "DESC" : "ASC",
        is_enabled: "1",
      }
    }

    return { is_enabled: "1" }
  }

  const getCSVExportMeta = () => {
    if (meta && (urlQueryParams.top15 || urlQueryParams.bottom15)) {
      return {
        ...meta,
        total: 15,
      }
    }

    return meta
  }

  const { handleExport } = useExportCSV({
    params: { ...urlQueryParams, ...getAdditionalParams() },
    headerCells: tableHeaderCells,
    meta: getCSVExportMeta(),
    fileName: "contractors",
    convertData: convertContractorsToTableRows,
    useLazyQuery: useLazyGetContractorsQuery,
  })

  const onAddClick = (event: FormEvent<HTMLElement>) => {
    event?.preventDefault()
    setShowAddContractorModal(true)
  }

  const setSearchParam = (param: "top15" | "bottom15") => {
    setUrlQueryParams({ [param]: true }, true)
  }

  const handleTop15Click = () => {
    setSearchParam("top15")
  }

  const handleBottom15Click = () => {
    setSearchParam("bottom15")
  }

  const handleAddContractorSuccess = () => {
    setShowAddContractorModal(false)
    onAddContractorSuccess()
  }

  return (
    <>
      <nav className={styles.tableNavWrapper}>
        <div>
          <ButtonNew onClick={onAddClick} color="blue">
            Add contractor
          </ButtonNew>
        </div>
        <div className={styles.tableNavButtonsWrapper}>
          <ButtonNew
            color="tropicalCyan"
            onClick={handleTop15Click}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            <TopArrow />
            Top 15
          </ButtonNew>
          <Bottom16Button
            color="tropicalCyan"
            onClick={handleBottom15Click}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            <ReverseArrow />
            Bottom 15
          </Bottom16Button>
          <EditColumnsButton tableName="admin_contractors_list" tableHeaderCells={tableHeaderCells} />
          <ButtonNew
            color="tropicalCyan"
            onClick={handleExport}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            Export
          </ButtonNew>
        </div>
      </nav>
      {showAddContractorModal && (
        <AddContractorModal onCancel={() => setShowAddContractorModal(false)} onSuccess={handleAddContractorSuccess} />
      )}
    </>
  )
}

export default Header
